import type { FC, SyntheticEvent } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import type { SelectChangeEvent, SxProps, Theme } from '@mui/material';
import { Autocomplete, Checkbox, CircularProgress, TextField, Typography } from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import type { Option, SelectProps } from 'components/common';
import { CommonSelect, PlaceholderNoAccess } from 'components/common';
import { useFormContext } from 'react-hook-form';
import { parseFlowsString, stringifyFlows } from 'utils';
import { type SelectFlowsParams, useInfiniteFlows } from '../api';
import type { Flow } from '../types';
import { useInView } from 'react-intersection-observer';
import { usePermissionGuard } from 'features/login';
import { trackCabinetQueryError } from 'lib/datadog';

interface SelectFlowsSx {
  /** Merged into `.MuiAutocomplete-root` styles when `asAutocomplete` is used. */
  root?: SxProps<Theme>;
}

interface SelectFlowsAttrs {
  open: boolean;
  multiples: boolean;
  /**
   * Renders a multi-select autocomplete showing the selected flow name (or
   * "Flows <N>" when several are selected) as plain text. The bound field
   * keeps storing a single comma-separated string (`"flowName,flowName"`)
   * instead of an array, so it stays compatible with consumers that treat
   * the flow field as plain text (e.g. `AdSetEntry.flow`).
   */
  multiple?: boolean;
  placeholder: string;
  params?: SelectFlowsParams;
  /** Defaults to flow id. Use `name` when the consumer stores a string flow name. */
  valueKey?: 'id' | 'name';
  sx?: SelectFlowsSx;
}

type SelectFlowsProps = Partial<
  Pick<
    SelectProps,
    | 'handleChange'
    | 'name'
    | 'inputLabel'
    | 'onClose'
    | 'disabled'
    | 'asAutocomplete'
    | 'size'
    | 'hideErrorMessage'
    | 'required'
  > &
  SelectFlowsAttrs
>;

const checkedIcon = <CheckBoxIcon fontSize="small" />;
const uncheckedIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;

const SelectFlowsComponent: FC<SelectFlowsProps> = (
  {
    handleChange,
    name,
    inputLabel,
    multiples,
    multiple,
    placeholder,
    onClose,
    open,
    params,
    disabled,
    asAutocomplete,
    valueKey = 'id',
    size,
    sx,
    hideErrorMessage,
    required,
  }) => {
  const fieldName = name ?? 'flows';
  const { watch, setValue } = useFormContext();
  const { ref, inView } = useInView({
    threshold: 0,
  });
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isError, error } =
    useInfiniteFlows({ params });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (!isError || !error) {
      return;
    }

    trackCabinetQueryError(error, 'facebook', 'flow');
  }, [isError, error]);

  const options = useMemo(() => {
    if (!data) return [];
    return data.pages.flatMap((page) =>
      page.data.map((flow: Flow) => ({
        label: flow.name,
        value: valueKey === 'name' ? flow.name : flow.id,
      })),
    );
  }, [data, valueKey]);

  const currentValue = watch(fieldName) as string | undefined;

  const selectedOptions = useMemo(() => {
    if (!multiple) return [];
    const selectedNames = parseFlowsString(currentValue);
    return options.filter((option) => selectedNames.includes(String(option.value)));
  }, [multiple, currentValue, options]);

  const handleMultipleChange = useCallback(
    (_: SyntheticEvent, newValue: Option[]) => {
      const joined = stringifyFlows(newValue.map((option) => String(option.value)));
      setValue(fieldName, joined, { shouldValidate: true });
      handleChange?.({ target: { value: joined } } as SelectChangeEvent<unknown>);
    },
    [fieldName, setValue, handleChange],
  );

  if (multiple) {
    return (
      <Autocomplete
        multiple
        size={size}
        disabled={disabled}
        options={options}
        value={selectedOptions}
        onChange={handleMultipleChange}
        onClose={onClose}
        open={open}
        loading={isFetchingNextPage}
        disableCloseOnSelect
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        renderOption={(props, option, state) => {
          const { key, ...otherProps } = props;
          return (
            <li key={key} {...otherProps}>
              <Checkbox
                icon={uncheckedIcon}
                checkedIcon={checkedIcon}
                checked={state.selected}
                sx={{ mr: 1 }}
              />
              {option.label}
            </li>
          );
        }}
        renderTags={(value) => {
          if (value.length === 0) return null;
          const label = value.length === 1 ? value[0].label : `Flows ${value.length}`;
          return (
            <Typography component="span" fontSize={14} sx={{ pl: 1.5 }}>
              {label}
            </Typography>
          );
        }}
        renderInput={(inputParams) => (
          <TextField
            {...inputParams}
            size={size}
            label={inputLabel}
            placeholder={selectedOptions.length ? undefined : placeholder}
            required={required}
            InputProps={{
              ...inputParams.InputProps,
              endAdornment: (
                <>
                  {isFetchingNextPage ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : null}
                  {inputParams.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        ListboxProps={{
          onScroll: (event) => {
            const listboxNode = event.currentTarget;
            if (
              listboxNode.scrollTop + listboxNode.clientHeight >=
              listboxNode.scrollHeight - 20
            ) {
              ref(listboxNode);
            }
          },
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            flexWrap: 'nowrap',
          },
          ...sx?.root,
        }}
      />
    );
  }

  return (
    <CommonSelect
      name={fieldName}
      inputLabel={inputLabel ?? 'Flows'}
      options={options}
      handleChange={handleChange}
      placeholder={placeholder}
      multiples={multiples}
      onClose={onClose}
      open={open}
      loading={isFetchingNextPage}
      loadMoreRef={ref}
      disabled={disabled}
      asAutocomplete={asAutocomplete}
      size={size}
      sx={sx?.root}
      hideErrorMessage={hideErrorMessage}
      required={required}
    />
  );
};

const SelectFlows: FC<SelectFlowsProps> = (props) => {
  const { accessToViewFlow } = usePermissionGuard();
  if (!accessToViewFlow)
    return <PlaceholderNoAccess title={'No access to view flow'} />;

  return <SelectFlowsComponent {...props} />;
};

export default SelectFlows;
